import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiUrl } from "@/lib/api-config";
import type {
  Discussion,
  DiscussionMessage,
  CreateDiscussionPayload,
  CreateMessagePayload,
  CollaboratorUserInfo,
} from "@/types";


interface UseDiscussionsReturn {
  discussions: Discussion[];
  messages: DiscussionMessage[];
  activeDiscussion: Discussion | null;
  collaborators: CollaboratorUserInfo[];
  isLoading: boolean;
  error: string | null;
  fetchDiscussions: (reviewId: string) => Promise<void>;
  fetchMessages: (discussionId: string) => Promise<void>;
  createDiscussion: (reviewId: string, payload: CreateDiscussionPayload) => Promise<Discussion | null>;
  addMessage: (discussionId: string, payload: CreateMessagePayload) => Promise<DiscussionMessage | null>;
  markAsRead: (discussionId: string) => Promise<void>;
  fetchCollaborators: (reviewId: string) => Promise<void>;
  setActiveDiscussion: (discussion: Discussion | null) => void;
  subscribeToDiscussion: (discussionId: string) => void;
  unsubscribeFromDiscussion: () => void;
}

export function useDiscussions(): UseDiscussionsReturn {
  const { getAccessToken, user } = useAuth();
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [messages, setMessages] = useState<DiscussionMessage[]>([]);
  const [activeDiscussion, setActiveDiscussion] = useState<Discussion | null>(null);
  const [collaborators, setCollaborators] = useState<CollaboratorUserInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addedMessageIdsRef = useRef<Set<string>>(new Set());
  const eventSourceRef = useRef<EventSource | null>(null);

  const fetchDiscussions = useCallback(async (reviewId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(apiUrl(`/api/reviews/${reviewId}/discussions`), {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to fetch discussions");
      }

      const data = await response.json();
      setDiscussions(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch discussions";
      setError(message);
      setDiscussions([]);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  const fetchMessages = useCallback(async (discussionId: string) => {
    setIsLoading(true);
    setError(null);
    addedMessageIdsRef.current.clear();

    try {
      const token = await getAccessToken();
      const response = await fetch(apiUrl(`/api/discussions/${discussionId}/messages`), {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to fetch messages");
      }

      const data: DiscussionMessage[] = await response.json();
      data.forEach((m) => addedMessageIdsRef.current.add(m.id));
      setMessages(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch messages";
      setError(message);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  const createDiscussion = useCallback(async (
    reviewId: string,
    payload: CreateDiscussionPayload
  ): Promise<Discussion | null> => {
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(apiUrl(`/api/reviews/${reviewId}/discussions`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create discussion");
      }

      setDiscussions((prev) => [data, ...prev]);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create discussion";
      setError(message);
      return null;
    }
  }, [getAccessToken]);

  const addMessage = useCallback(async (
    discussionId: string,
    payload: CreateMessagePayload
  ): Promise<DiscussionMessage | null> => {
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(apiUrl(`/api/discussions/${discussionId}/messages`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to add message");
      }

      if (!addedMessageIdsRef.current.has(data.id)) {
        addedMessageIdsRef.current.add(data.id);
        setMessages((prev) => [...prev, data]);
      }

      setDiscussions((prev) =>
        prev.map((d) =>
          d.id === discussionId
            ? { ...d, message_count: (d.message_count || 0) + 1, last_message_at: data.created_at }
            : d
        )
      );

      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add message";
      setError(message);
      return null;
    }
  }, [getAccessToken]);

  const markAsRead = useCallback(async (discussionId: string) => {
    try {
      const token = await getAccessToken();
      await fetch(apiUrl(`/api/discussions/${discussionId}/read`), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      setDiscussions((prev) =>
        prev.map((d) => (d.id === discussionId ? { ...d, is_unread: false } : d))
      );
    } catch (err) {
      console.error("Failed to mark as read:", err);
    }
  }, [getAccessToken]);

  const fetchCollaborators = useCallback(async (reviewId: string) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(apiUrl(`/api/reviews/${reviewId}/accessible-users`), {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          typeof data === "object" && data && "error" in data
            ? String((data as { error: string }).error)
            : "Failed to fetch accessible users"
        );
      }

      const data = (await response.json()) as CollaboratorUserInfo[];
      setCollaborators(data);
    } catch (err) {
      console.error("Failed to fetch accessible users:", err);
      setCollaborators([]);
    }
  }, [getAccessToken]);

  const subscribeToDiscussion = useCallback(
    async (discussionId: string) => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const token = await getAccessToken();
      const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : "";
      const url = apiUrl(`/api/discussions/${discussionId}/stream${tokenQuery}`);
      const es = new EventSource(url);
      eventSourceRef.current = es;

    es.addEventListener("message", (event) => {
      try {
        const newMessage = JSON.parse(event.data) as DiscussionMessage;

        if (addedMessageIdsRef.current.has(newMessage.id)) return;
        if (newMessage.user_id === user?.id) return;

        addedMessageIdsRef.current.add(newMessage.id);

        setMessages((prev) => {
          if (prev.some((m) => m.id === newMessage.id)) return prev;
          return [...prev, newMessage];
        });

        setDiscussions((prev) =>
          prev.map((d) =>
            d.id === discussionId
              ? { ...d, message_count: (d.message_count || 0) + 1, last_message_at: newMessage.created_at }
              : d
          )
        );
      } catch (err) {
        console.error("Failed to parse SSE message:", err);
      }
    });

    es.onerror = () => {
      console.warn("[discussions] SSE connection error");
    };
  }, [user?.id, getAccessToken]);

  const unsubscribeFromDiscussion = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return {
    discussions,
    messages,
    activeDiscussion,
    collaborators,
    isLoading,
    error,
    fetchDiscussions,
    fetchMessages,
    createDiscussion,
    addMessage,
    markAsRead,
    fetchCollaborators,
    setActiveDiscussion,
    subscribeToDiscussion,
    unsubscribeFromDiscussion,
  };
}
